import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { RotateCcw, Loader2, X, ExternalLink } from 'lucide-react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

// Inner component that holds modal state - remounts when modal opens for fresh state
function RefundModalContent({ onClose, onConfirm, course, refundAmount, isLoading }) {
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);

  return (
    <div className="flex items-center justify-center p-4 h-full">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="modal-panel relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
            <RotateCcw className="w-5 h-5 text-[#589DD5]" />
            <h2 className="text-lg font-semibold">Request Refund</h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Course Info */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">Course</p>
            <p className="font-medium text-gray-900 dark:text-white">{course.course_title}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
              Refund Amount: ${refundAmount.toFixed(2)}
            </p>
          </div>

          {/* Processing Time Notice */}
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">Processing Time</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Refunds are typically processed within 5-10 business days.
              The refund will be credited to your original payment method.
            </p>
          </div>

          {/* Refund Policy Summary */}
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p className="font-medium text-gray-800 dark:text-gray-200">Important</p>
            <ul className="list-disc ml-5 space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <li>This action cannot be undone.</li>
              <li>Your course enrollment will be cancelled upon refund.</li>
            </ul>
            <a
              href="/tos#course-enrollment-payment-terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#589DD5] hover:text-[#4a8bc4] mt-1"
            >
              View full refund policy
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Agreement Checkbox */}
          <label className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreedToPolicy}
              onChange={(e) => setAgreedToPolicy(e.target.checked)}
              disabled={isLoading}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-[#589DD5] focus:ring-[#589DD5] cursor-pointer dark:bg-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              I have read and agree to the{' '}
              <a
                href="/tos#course-enrollment-payment-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#589DD5] hover:text-[#4a8bc4] underline"
                onClick={(e) => e.stopPropagation()}
              >
                refund policy
              </a>
              . I understand this action is permanent and my enrollment will be cancelled.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 px-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!agreedToPolicy || isLoading}
            className="flex-1 py-2.5 px-4 bg-[#589DD5] hover:bg-[#4a8bc4] text-white font-medium rounded-lg transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm Refund'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wrapper component that handles portal and animated transitions
// State resets automatically when RefundModalContent unmounts/remounts
export default function RefundModal({ isOpen, onClose, onConfirm, course, refundAmount, isLoading }) {
  const nodeRef = useRef(null);
  useBodyScrollLock(isOpen);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames="modal-animate"
      unmountOnExit
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="modal-animate" style={{ zIndex: 200 }}>
        <RefundModalContent
          onClose={onClose}
          onConfirm={onConfirm}
          course={course}
          refundAmount={refundAmount}
          isLoading={isLoading}
        />
      </div>
    </CSSTransition>,
    document.body
  );
}
